import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import clsx from "clsx";
import { FiMenu, FiX } from "react-icons/fi";

import Button from "@/components/ui/Button";
import { ROUTES } from "@/config/constants";
import { useAuthStore } from "@/store/authStore";
import { getAllBookings } from "@/services/booking.service";

type DashboardVariant = "customer" | "admin";

type NavItem = {
  to: string;
  label: string;
  show?: boolean;
  badge?: number;
};

type DashboardLayoutProps = {
  variant: DashboardVariant;
  children: React.ReactNode;
};

const linkBase =
  "flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors";

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ variant, children }) => {
  useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Fetch pending approvals count for admin variant
  useEffect(() => {
    if (variant !== "admin") return;

    const fetchPendingCount = async () => {
      try {
        const bookings = await getAllBookings();
        const pending = bookings.filter((b) => b.status === "pending-approval");
        setPendingCount(pending.length);
      } catch (error) {
        console.error("Error fetching pending approvals count:", error);
      }
    };

    fetchPendingCount();

    // Refresh count every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);

    return () => clearInterval(interval);
  }, [variant]);

  const items = useMemo<NavItem[]>(() => {
    if (variant === "admin") {
      return [
        { to: ROUTES.ADMIN_DASHBOARD, label: "Dashboard" },
        {
          to: ROUTES.ADMIN_PENDING_APPROVALS,
          label: "Pending Approvals",
          badge: pendingCount,
        },
        { to: ROUTES.ADMIN_BOOKINGS, label: "Manage Bookings" },
        { to: ROUTES.ADMIN_INVOICES, label: "Invoices" },
        { to: ROUTES.ADMIN_DELIVERABLES, label: "Deliverables" },
        { to: ROUTES.ADMIN_SERVICES, label: "Services" },
        { to: ROUTES.ADMIN_PACKAGES, label: "Packages" },
        { to: ROUTES.ADMIN_PORTFOLIO, label: "Portfolio" },
        { to: ROUTES.ADMIN_SHOWROOM, label: "Showroom" },
        { to: ROUTES.ADMIN_REVIEWS, label: "Reviews" },
        { to: ROUTES.ADMIN_COUPONS, label: "Coupons" },
        { to: ROUTES.ADMIN_ANALYTICS, label: "Analytics" },
        { to: ROUTES.ADMIN_EMAIL_SETTINGS, label: "Email Settings" },
      ];
    }

    return [
      { to: ROUTES.CUSTOMER_DASHBOARD, label: "Dashboard" },
      { to: ROUTES.CUSTOMER_BOOKINGS, label: "Bookings" },
      { to: ROUTES.CUSTOMER_DELIVERABLES, label: "Deliverables" },
      { to: ROUTES.CUSTOMER_INVOICES, label: "Invoices" },
      { to: ROUTES.CUSTOMER_PROFILE, label: "Profile" },
    ];
  }, [variant, pendingCount]);

  const visibleItems = useMemo(() => items.filter((item) => item.show !== false), [items]);

  const Sidebar = (
    <nav className="space-y-1">
      {visibleItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            clsx(
              linkBase,
              isActive
                ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200"
                : "text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
            )
          }
          end={item.to === ROUTES.CUSTOMER_DASHBOARD || item.to === ROUTES.ADMIN_DASHBOARD}
        >
          <span className="truncate">{item.label}</span>
          {item.badge !== undefined && item.badge > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-600 rounded-full">
              {item.badge}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div>
      {/* Mobile top bar */}
      <div className="md:hidden flex items-center justify-between mb-4">
        <Button variant="outline" size="sm" onClick={() => setMobileOpen(true)}>
          <FiMenu className="mr-2" />
          Menu
        </Button>
        {variant === "admin" && (
          <span className="text-sm font-medium text-neutral-600 dark:text-neutral-300">Admin</span>
        )}
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-bold text-neutral-900 dark:text-white">
                {variant === "admin" ? "Admin" : "Dashboard"}
              </div>
              <button
                type="button"
                className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            {Sidebar}
          </div>
        </div>
      )}

      <div className="flex gap-6">
        {/* Desktop sidebar */}
        <aside className="hidden md:block w-64 shrink-0">
          <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-4 sticky top-24">
            <div className="text-sm font-semibold text-neutral-900 dark:text-white mb-3">
              {variant === "admin" ? "Admin" : "Dashboard"}
            </div>
            {Sidebar}
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
};

export default DashboardLayout;
